import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { Dispatch } from "redux";
import {
  showApiProgress,
  hideApiProgress,
  updateApiProgress,
  resetApiProgress,
} from "../store/slices/ui";
import translate from "./translations";

/**
 * Configuration options for API progress tracking
 */
export interface ApiProgressConfig {
  /**
   * Whether to track upload progress
   * @default true
   */
  trackUpload?: boolean;

  /**
   * Whether to track download progress
   * @default true
   */
  trackDownload?: boolean;

  /**
   * Custom message to display during the request
   */
  message?: string;

  /**
   * Minimum duration (ms) to show the progress indicator
   * Prevents flickering for very fast requests
   * @default 300
   */
  minDuration?: number;

  /**
   * Whether to enable progress tracking for this request
   * @default true
   */
  enabled?: boolean;
}

/**
 * Setup Axios interceptors to automatically track API request progress
 *
 * @param axiosInstance - The Axios instance to attach interceptors to
 * @param dispatch - Redux dispatch function
 *
 * @example
 * ```typescript
 * import { store } from './store';
 * import axios from 'axios';
 *
 * setupApiProgressInterceptor(axios, () => store.dispatch);
 * ```
 */
export const setupApiProgressInterceptor = (
  axiosInstance: AxiosInstance,
  getDispatch: () => Dispatch,
) => {
  // Track active requests
  let activeRequests = 0;
  let progressStartTime = 0;

  // Helper to get dispatch lazily
  const getDispatchSafe = () => getDispatch();

  // Request interceptor
  axiosInstance.interceptors.request.use(
    (config: any) => {
      const progressConfig: ApiProgressConfig = config.apiProgress || {};
      const method = config.method?.toUpperCase();

      // Determine if progress tracking should be enabled for this request
      // Enabled for POST requests by default, but can be overridden
      let shouldTrack = method === "POST";

      if (progressConfig.enabled !== undefined) {
        shouldTrack = progressConfig.enabled;
      }

      // Store the tracking state on the config to use in response/error interceptors
      config.__shouldTrackProgress = shouldTrack;

      if (!shouldTrack) {
        return config;
      }

      activeRequests++;

      if (activeRequests === 1) {
        progressStartTime = Date.now();
        getDispatchSafe()(
          showApiProgress({
            message:
              progressConfig.message || translate("Loading_Text", "Loading..."),
          }),
        );
      }

      // Setup upload progress tracking
      if (progressConfig.trackUpload !== false && config.data) {
        config.onUploadProgress = (progressEvent: any) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            getDispatchSafe()(
              updateApiProgress({
                progress: percentCompleted,
                message:
                  progressConfig.message ||
                  translate("Uploading_Text", "Uploading..."),
              }),
            );
          }
        };
      }

      // Setup download progress tracking
      if (progressConfig.trackDownload !== false) {
        config.onDownloadProgress = (progressEvent: any) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            getDispatchSafe()(
              updateApiProgress({
                progress: percentCompleted,
                message:
                  progressConfig.message ||
                  translate("Downloading_Text", "Downloading..."),
              }),
            );
          } else {
            // If total is unknown, show indeterminate progress
            const loaded = progressEvent.loaded;
            const progress = Math.min(90, (loaded / 1024 / 1024) * 10); // Estimate based on MB
            getDispatchSafe()(
              updateApiProgress({
                progress,
                message:
                  progressConfig.message ||
                  translate("Loading_Text", "Loading..."),
              }),
            );
          }
        };
      }

      return config;
    },
    (error) => {
      // Only decrement if the request was being tracked
      if (error.config?.__shouldTrackProgress) {
        activeRequests = Math.max(0, activeRequests - 1);
        if (activeRequests === 0) {
          getDispatchSafe()(hideApiProgress());
        }
      }
      return Promise.reject(error);
    },
  );

  // Response interceptor
  axiosInstance.interceptors.response.use(
    async (response: AxiosResponse) => {
      // Only process if the request was being tracked
      if ((response.config as any).__shouldTrackProgress) {
        activeRequests = Math.max(0, activeRequests - 1);

        if (activeRequests === 0) {
          const config: ApiProgressConfig =
            (response.config as any).apiProgress || {};
          const minDuration = config.minDuration || 300;
          const elapsed = Date.now() - progressStartTime;

          // Ensure minimum duration to prevent flickering
          if (elapsed < minDuration) {
            await new Promise((resolve) =>
              setTimeout(resolve, minDuration - elapsed),
            );
          }

          // Complete the progress
          getDispatchSafe()(updateApiProgress({ progress: 100 }));

          // Hide after a brief moment
          setTimeout(() => {
            getDispatchSafe()(resetApiProgress());
          }, 200);
        }
      }

      return response;
    },
    async (error) => {
      // Only process if the request was being tracked
      if (error.config?.__shouldTrackProgress) {
        activeRequests = Math.max(0, activeRequests - 1);

        if (activeRequests === 0) {
          const config: ApiProgressConfig = error.config?.apiProgress || {};
          const minDuration = config.minDuration || 300;
          const elapsed = Date.now() - progressStartTime;

          // Ensure minimum duration
          if (elapsed < minDuration) {
            await new Promise((resolve) =>
              setTimeout(resolve, minDuration - elapsed),
            );
          }

          getDispatchSafe()(resetApiProgress());
        }
      }

      return Promise.reject(error);
    },
  );
};

/**
 * Extend Axios request config to include progress tracking options
 */
declare module "axios" {
  export interface AxiosRequestConfig {
    apiProgress?: ApiProgressConfig;
    __shouldTrackProgress?: boolean;
  }
}
